import { useEffect, useRef } from 'react';
import { useSafeBack } from '../hooks/useSafeBack';
import { SAFE_BACK_EVENT } from '../hooks/useUnsavedChangesGuard';
import { useLocation, useNavigate } from 'react-router-dom';
import { sessionBack } from './MobileNavigation';

const EDGE_WIDTH = 24;
const MIN_SWIPE_X = 60;
const MAX_VERTICAL_BIAS = 40;

const INTERACTIVE_SELECTOR = [
  'a',
  'button',
  'input',
  'textarea',
  'select',
  '[contenteditable="true"]',
  '[role="button"]',
  '[role="link"]',
  '[role="slider"]',
  '[role="tab"]',
  '[role="switch"]',
  '[role="checkbox"]',
  '[role="radio"]',
  '[data-edge-swipe-ignore="true"]',
].join(',');

const isIosNativeWebView = () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }

  const capacitor = window.Capacitor;
  const platform = capacitor?.getPlatform?.();
  const isNative = capacitor?.isNativePlatform?.();

  if (platform) {
    return platform === 'ios' && isNative !== false;
  }

  const ua = navigator.userAgent || '';
  const isiOS = /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  return isiOS && Boolean(capacitor);
};

const isHorizontalScroller = (element) => {
  let node = element;

  while (node && node !== document.body && node !== document.documentElement) {
    if (node instanceof HTMLElement) {
      const style = window.getComputedStyle(node);
      const overflowX = style.overflowX;
      const scrollsX = node.scrollWidth > node.clientWidth + 1;
      const allowsHorizontalScroll = overflowX === 'auto' || overflowX === 'scroll';

      if (scrollsX && allowsHorizontalScroll) {
        return true;
      }

      if (
        node.getAttribute('role') === 'table' ||
        node.getAttribute('role') === 'grid' ||
        node.matches('[data-carousel], [data-slider], table')
      ) {
        return true;
      }
    }

    node = node.parentElement;
  }

  return false;
};

const shouldIgnoreTarget = (target) => {
  if (!(target instanceof Element)) {
    return true;
  }

  return Boolean(target.closest(INTERACTIVE_SELECTOR)) || isHorizontalScroller(target);
};

const IosEdgeSwipeBack = () => {
  const { goBack } = useSafeBack('/dashboard');
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const gestureRef = useRef(null);

  useEffect(() => {
    if (!isIosNativeWebView()) {
      return undefined;
    }

    const handleTouchStart = (event) => {
      if (event.touches.length !== 1) {
        gestureRef.current = null;
        return;
      }

      const touch = event.touches[0];
      if (touch.clientX > EDGE_WIDTH || shouldIgnoreTarget(event.target)) {
        gestureRef.current = null;
        return;
      }

      gestureRef.current = {
        startX: touch.clientX,
        startY: touch.clientY,
        triggered: false,
      };
    };

    const handleTouchMove = (event) => {
      const gesture = gestureRef.current;
      if (!gesture || gesture.triggered || event.touches.length !== 1) {
        return;
      }

      const touch = event.touches[0];
      const dx = touch.clientX - gesture.startX;
      const dy = Math.abs(touch.clientY - gesture.startY);

      if (dy > Math.max(MAX_VERTICAL_BIAS, Math.abs(dx))) {
        gestureRef.current = null;
        return;
      }

      if (dx >= MIN_SWIPE_X && dy <= Math.abs(dx) * 0.6) {
        gesture.triggered = true;
        if (/^\/games\/[^/]+$/.test(pathname)) {
          sessionBack(navigate);
          return;
        }
        const action = goBack;
        const safeBackEvent = new CustomEvent(SAFE_BACK_EVENT, {
          cancelable: true,
          detail: { action },
        });

        if (window.dispatchEvent(safeBackEvent)) {
          action();
        }
      }
    };

    const clearGesture = () => {
      gestureRef.current = null;
    };

    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: true });
    document.addEventListener('touchend', clearGesture, { passive: true });
    document.addEventListener('touchcancel', clearGesture, { passive: true });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', clearGesture);
      document.removeEventListener('touchcancel', clearGesture);
    };
  }, [goBack, navigate, pathname]);

  return null;
};

export default IosEdgeSwipeBack;
